import { Component, Input, OnInit } from '@angular/core';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';

import allianzContent from 'src/app/pages/admin/dashboard/home.content.allianz';
import eduContent from 'src/app/pages/admin/dashboard/home.content.edu';
import feusiContent from 'src/app/pages/admin/dashboard/home.content.feusi';
import feusiAcademyContent from 'src/app/pages/admin/dashboard/home.content.feusi-academy';
import swissManagementInstituteContent from 'src/app/pages/admin/dashboard/home.content.swiss-management-institute';
import vettrustContent from 'src/app/pages/admin/dashboard/home.content.vettrust';
import emptyContent from 'src/app/pages/admin/dashboard/home.content.empty';
import klingeContent from 'src/app/pages/admin/dashboard/home.content.klinge';
import mdooContent from 'src/app/pages/admin/dashboard/home.content.mdoo';
import minContent from 'src/app/pages/admin/dashboard/home.content.min';
import exampleContent from 'src/app/pages/admin/dashboard/home.content.example';
import { AggregationGroup } from 'src/app/services/jsonrpc/stats-server.service';
import { AnalyticsMetric } from 'src/app/services/stats.service';
import { Duration } from 'src/app/services/jsonrpc/user-server.service';
import { environment } from 'src/environments/environment';

// Create and add one for each new client
const dashboards: { [key: string]: Dashboard } = {
  min: minContent,
  allianz: allianzContent,
  edu: eduContent,
  mdoo: mdooContent,
  klinge: klingeContent,
  empty: emptyContent,
  feusi: feusiContent,
  feusiAcademy: feusiAcademyContent,
  vettrust: vettrustContent,
  example: exampleContent,
  'swiss-management-institute': swissManagementInstituteContent
};

function sub(date, timeObject) {
  const { years = 0, months = 0, weeks = 0, days = 0, hours = 0, minutes = 0, seconds = 0 } = timeObject;

  const newDate = new Date(date);
  newDate.setFullYear(newDate.getFullYear() - years);
  newDate.setMonth(newDate.getMonth() - months);
  newDate.setDate(newDate.getDate() - (weeks * 7 + days));
  newDate.setHours(newDate.getHours() - hours);
  newDate.setMinutes(newDate.getMinutes() - minutes);
  newDate.setSeconds(newDate.getSeconds() - seconds);

  return newDate;
}

function addMinutes(date, minutes) {
  const newDate = new Date(date);
  newDate.setTime(newDate.getTime() + minutes * 60000);
  return newDate;
}

export interface Dashboard {
  widgets: Widget[];
  labels?: string[]; // exposed for filters
}

export interface Widget {
  size?: number;
  class?: string;
  component: string;
  settings?: {
    title?: string;
    subtitle?: string;
    metric?: AnalyticsMetric;
    selector?: AggregationGroup;
    filter?: { [key: string | AggregationGroup]: string };
    datasets?: string | AggregationGroup | (string | AggregationGroup)[];
    grouping?: string | AggregationGroup | (string | AggregationGroup)[];
    timespan?: { days?: number; weeks?: number; months?: number; years?: number };
    customize?: boolean;
    [property: string | symbol]: unknown;
  };
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
  public dashboard: Dashboard;

  public fromDate: Date | null = null;
  public toDate: Date;
  public labels: { [key: string | AggregationGroup]: string } = {};

  public showFilter = false;
  private timeZoneOffset: number;

  constructor(private toastr: ToastrService) {
    this.timeZoneOffset = new Date().getTimezoneOffset();
  }

  public static fromOrMaxDelta(from: Date | null, to: Date, off: Duration | null): Date {
    if (!off) {
      return from;
    }
    const min = sub(to, off);
    return from && from > min ? from : min;
  }

  public ngOnInit(): void {
    this.resetFilters();
    this.initComponents();
  }

  public onFilterUpdated(filter: [NgbDate, NgbDate, { [key: string | AggregationGroup]: string }]): void {
    const fromDate: NgbDate = filter[0];
    const toDate: NgbDate = filter[1];
    if (
      fromDate?.year > toDate?.year ||
      (fromDate?.year === toDate?.year && fromDate?.month > toDate?.month) ||
      (fromDate?.year === toDate?.year && fromDate?.month === toDate?.month && fromDate?.day > toDate?.day)
    ) {
      this.toastr.error('Invalid date range selected');
      return;
    }
    const fixedTo = this.ngbToDate(filter[1]) ?? new Date();
    this.fromDate = this.ngbToDate(filter[0]);
    this.toDate = new Date(fixedTo.getFullYear(), fixedTo.getMonth(), fixedTo.getDate(), 23, 59, 59);
    this.labels = filter[2] ?? {};
    this.initComponents();
  }

  public resetFilters() {
    this.fromDate = null;
    this.toDate = this.today();
  }

  public isFiltered() {
    return this.fromDate != null || this.toDate != this.today();
  }

  public toggleFilter() {
    this.showFilter = !this.showFilter;
    if (!this.showFilter) {
      this.resetFilters();
      this.initComponents();
    }
  }

  public today() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  }

  public toUtc(date?: Date) {
    return date ? addMinutes(date, -this.timeZoneOffset) : date;
  }

  public async initComponents() {
    const from = this.toUtc(this.fromDate);
    const to = this.toDate;

    const preset = dashboards[environment.dashboard];
    const clone = preset.widgets.map(base => {
      const widget = JSON.parse(JSON.stringify(base));
      if (!widget.settings) widget.settings = {};
      // Setup start and end date, respect components that work on fixed intervalls
      widget.settings.to = to;
      widget.settings.from = DashboardComponent.fromOrMaxDelta(from, to, widget.settings.timespan);
      if (widget.settings.timedelta) {
        widget.settings.to = sub(widget.settings.to, widget.settings.timedelta);
        widget.settings.from = sub(widget.settings.from, widget.settings.timedelta);
      }
      // Inject additional filters from selected labels
      if (!widget.settings.filter) widget.settings.filter = {};
      Object.keys(this.labels).forEach(label => (widget.settings.filter['label_' + label] = this.labels[label]));
      return widget;
    });

    this.dashboard = { widgets: clone, labels: preset.labels };
  }

  public classFor(comp): string {
    if (comp.class) return comp.class;
    else if (comp.size > 0) return 'col-' + comp.size;
    else return 'col-12 col-xl-6';
  }

  public ngbToDate(date: NgbDate | null): Date | null {
    return date ? new Date(date.year, date.month - 1, date.day) : null;
  }

  public dateToNgb(date: Date | null): NgbDate | null {
    return date ? new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate()) : null;
  }
}
